import { getInstance } from "../instance";
import { Role, RoleAction, RolesResponse } from "./types";

/**
 * Slice 3 (ADR-004/007). DS role-management calls for the back-office page.
 *
 * DS exposes the auth endpoints (`POST /auth/roles`, `GET /auth/roles/:email`,
 * and the other internal endpoints like `/auth/validate-credentials`) at the
 * **un-versioned root**, matching how BBA, afy-auth, and the staging tests call
 * them. The shared `dsInstance` baseURL however carries the `/v1` prefix used
 * by the customer-facing routes (offboarding, customer-information, etc.) —
 * combining the two yields `/v1/auth/roles/...` which 404s.
 *
 * So we build absolute URLs whose base has the trailing `/v1` stripped, e.g.
 * `https://ds.<env>.authorify.com/auth/roles/...`. When VITE_DIGITAL_SERVICES_URL
 * is unset (tests), the base is empty and the URL falls back to `/auth/roles/...`
 * which is what the unit tests assert against.
 */
const DS_AUTH_BASE = (
  (import.meta.env.VITE_DIGITAL_SERVICES_URL ?? "") as string
)
  .replace(/\/+$/, "")
  .replace(/\/v1$/, "");
const ROLES_URL = `${DS_AUTH_BASE}/auth/roles`;

const instance = getInstance();

export const getRoles = async (email: string): Promise<Role[]> => {
  const url = `${ROLES_URL}/${encodeURIComponent(email)}`;
  return instance
    .get<RolesResponse>(url)
    .then((response) => response.data.roles)
    .catch((error) => Promise.reject(error));
};

export const manageRole = async (
  email: string,
  role: Role,
  action: RoleAction,
  actorEmail: string
): Promise<Role[]> => {
  const body = { email, role, action, actorEmail };
  return instance
    .post<RolesResponse>(ROLES_URL, body)
    .then((response) => response.data.roles)
    .catch((error) => Promise.reject(error));
};
